import { PageHeader } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { EpisodeCategory } from '@common/enums';
import type { IServiceConsumer } from '@frontend/components/interfaces/serviceConsumer';
import type { IReduxState } from '@frontend/configureStore';
import { serviceCodeButtonOrder } from '@common/customControls/ggzConsultServiceCodeSortOrder';
import { ManagerAuthorization, ServiceType } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { SettingsCategoryFilterButtonRow } from '@frontend/components/elements/SettingsCategoryFilterButtonRow';
import { assertHasAuthorization } from '@frontend/utils';
import { PersonalSettingsPreferenceListConsultationSelectProviders } from './PersonalSettingsPreferenceListConsultationSelectProviders';
import { fetchPreferenceListServiceConsumers } from './personalSettingsPreferenceListHelper';
import { PersonalSettingsPreferenceListReferralSelectProviders } from './PersonalSettingsPreferenceListReferralSelectProviders';
import { PersonalSettingsPreferenceListExaminationSelectProviders } from './PreferenceListExamination/PersonalSettingsPreferenceListExaminationSelectProviders';

export type PersonalSettingsPreferenceListConsumerContainerProps = {
    isReadOnly: boolean;
    organizationId?: string;
};

export function PersonalSettingsPreferenceListConsumerContainer(
    props: PersonalSettingsPreferenceListConsumerContainerProps
) {
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const translate = getLanguage();
    const { episodeCategory } = useParams() as { episodeCategory: EpisodeCategory };
    const { employmentId, organizationId: urlOrganizationId } = useParams() as {
        employmentId: string;
        organizationId: string;
    };

    const organizationId = props.organizationId || urlOrganizationId || '';

    if (isManager) {
        assertHasAuthorization(ManagerAuthorization.organizationRead);
    }

    const [serviceConsumers, setServiceConsumers] = useState<IServiceConsumer[]>([]);
    const [selectedServiceConsumer, setSelectedServiceConsumer] = useState<IServiceConsumer>();

    useEffect(() => {
        if (organizationId) {
            loadServiceConsumers();
        }
    }, [organizationId, episodeCategory]);

    if (serviceConsumers.length === 0) {
        return null;
    }

    return (
        <>
            <PageHeader title={getTitle()} />

            {serviceConsumers.length > 1 && (
                <SettingsCategoryFilterButtonRow
                    collection={serviceConsumers.map((consumer) => ({
                        displayText: consumer.service.name,
                        ...consumer,
                    }))}
                    onClick={setSelectedServiceConsumer}
                    selectedItem={selectedServiceConsumer}
                />
            )}

            {selectedServiceConsumer?.service.serviceType === ServiceType.referral && (
                <PersonalSettingsPreferenceListReferralSelectProviders
                    isReadOnly={props.isReadOnly}
                    key={selectedServiceConsumer.id}
                    organizationId={organizationId}
                    serviceConsumerId={selectedServiceConsumer.id}
                />
            )}

            {selectedServiceConsumer?.service.serviceType === ServiceType.examination && (
                <PersonalSettingsPreferenceListExaminationSelectProviders
                    isReadOnly={props.isReadOnly}
                    key={selectedServiceConsumer.id}
                    organizationId={organizationId}
                    serviceConsumerId={selectedServiceConsumer.id}
                />
            )}

            {selectedServiceConsumer?.service.serviceType === ServiceType.consultation && (
                <PersonalSettingsPreferenceListConsultationSelectProviders
                    isReadOnly={props.isReadOnly}
                    key={selectedServiceConsumer.id}
                    organizationId={organizationId}
                    serviceConsumerId={selectedServiceConsumer.id}
                />
            )}
        </>
    );

    async function loadServiceConsumers() {
        const consumers = (await fetchPreferenceListServiceConsumers(
            organizationId,
            episodeCategory,
            props.isReadOnly,
            employmentId
        )) as IServiceConsumer[];

        const sortedConsumers = sortBy(
            consumers,
            (consumer: IServiceConsumer) => serviceCodeButtonOrder.indexOf(consumer.service.code!),
            (consumer: IServiceConsumer) => consumer.service.name
        );

        if (sortedConsumers.length > 0) {
            setSelectedServiceConsumer(sortedConsumers[0]);
        }

        setServiceConsumers(sortedConsumers);
    }

    function getTitle() {
        const providers = translate.episodeCategory.executors;
        const episodeCategoryTranslation = translate.episodeCategoryEnum[episodeCategory];
        const preferenceList = translate.personalSettings.preferenceList.preferenceLists;

        let title = `${preferenceList} ${episodeCategoryTranslation}`;

        if (isManager) {
            title += ` ${providers}`;
        }

        return title;
    }
}
